import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import type { OutlinedInputProps } from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import { createFilterOptions, type CreateFilterOptionsConfig } from '@mui/material/useAutocomplete';
import React from 'react';
import { uid } from 'react-uid';
import styled from 'styled-components';
import type { InputComponentProps, ValueComponentProps } from '../../design-system';
import { basicStringify } from '../../utils/format';
import type { FormValues, HelperText } from '../../utils/formUtils';
import { getNestedHelperText } from '../../utils/formUtils';
import { AutocompleteOption } from '../AutocompleteOption';
import { TextField } from '../TextField';

export type AutocompleteContents<T> = ValueComponentProps<T> & {
  label?: T extends (infer E)[] ? (element: E) => string : string;
};

export interface AutocompleteInfo<T = string> extends AutocompleteContents<T> {
  disabled?: boolean;
  group?: string;
}

export const AutocompleteWrapper = styled('div')`
  width: 100%;

  .textfield-root > * {
    padding: ${(p) => p.theme.spacing(0, 1)};
  }
`;

interface AutocompleteInputProps<T = string> extends Omit<InputComponentProps<T>, 'checked'> {
  useNullAsDefaultValue?: boolean;
  filterOptions?: CreateFilterOptionsConfig<AutocompleteInfo<T>>;
  options: AutocompleteInfo<T>[];
  InputProps?: Partial<OutlinedInputProps>;
}

function getValue<T = string>({
  options,
  useNullAsDefaultValue,
  value,
}: Pick<AutocompleteInputProps<T>, 'options' | 'useNullAsDefaultValue' | 'value'>): AutocompleteInfo<T> | null {
  const valueInOptions = options.find((option) => option.value === value);
  if (valueInOptions) {
    return valueInOptions;
  }
  if (useNullAsDefaultValue) {
    return null;
  }
  return options[0];
}

export const renderAutocompleteInput: (
  disabled: boolean,
  error: boolean,
  helperText: HelperText<FormValues>,
  InputProps?: OutlinedInputProps,
) => React.VFC<AutocompleteRenderInputParams> =
  (disabled, error, helperText, overrideInputProps) =>
  ({ id, fullWidth, InputLabelProps, InputProps, inputProps }: AutocompleteRenderInputParams) =>
    (
      <TextField
        InputLabelProps={InputLabelProps}
        InputProps={{ ...InputProps, ...overrideInputProps }}
        className="textfield-root"
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        helperText={getNestedHelperText(helperText)}
        id={id}
        inputElementProps={inputProps}
      />
    );

export function AutocompleteValue<T>({ label, value }: AutocompleteContents<T>): React.ReactElement {
  if (Array.isArray(value) && typeof label === 'function') {
    return (
      <List dense>
        {value.map((v, idx) => (
          <ListItem style={{ paddingLeft: 0 }} key={uid(v, idx)}>
            {label(v)}
          </ListItem>
        ))}
      </List>
    );
  }

  const displayText = typeof label === 'string' ? label : basicStringify(value);

  return (
    <Typography color="textPrimary" variant="body2">
      {displayText}
    </Typography>
  );
}

export function AutocompleteInput<T = string>({
  disabled,
  error,
  filterOptions,
  helperText,
  InputProps,
  name,
  onBlur,
  onChange,
  options,
  useNullAsDefaultValue,
  value,
}: AutocompleteInputProps<T>): React.ReactElement {
  const filter = createFilterOptions(filterOptions);

  const getOptionLabel = React.useCallback(
    (option: AutocompleteInfo<T>): string => {
      if (filterOptions?.stringify) {
        return filterOptions.stringify(option);
      }
      if (typeof option.label === 'function') {
        return option.label(option.value);
      }
      return option.label ?? basicStringify(option.value);
    },
    [filterOptions],
  );

  return (
    <AutocompleteWrapper>
      <Autocomplete
        disableClearable={!!value}
        disabled={disabled}
        filterOptions={(allOptions, state) => filter(allOptions, state)}
        getOptionDisabled={(option) => !!option.disabled}
        getOptionLabel={getOptionLabel}
        handleHomeEndKeys
        isOptionEqualToValue={(option, itemToCheck) => option.value === itemToCheck.value}
        onBlur={onBlur}
        onChange={(event, changedValue) =>
          onChange(name)({ ...event, target: { ...event.target, value: changedValue?.value ?? null } })
        }
        options={options}
        renderInput={renderAutocompleteInput(disabled, error, helperText, InputProps)}
        renderOption={(
          {
            'aria-disabled': ariaDisabled,
            'aria-selected': ariaSelected,
            className,
            onClick,
            onMouseMove,
            onTouchStart,
            role,
            tabIndex,
          },
          option,
          state,
        ) => (
          <AutocompleteOption
            aria-disabled={ariaDisabled}
            aria-selected={ariaSelected}
            className={className}
            index={state.index}
            key={uid(option)}
            onClick={onClick}
            onMouseMove={onMouseMove}
            onTouchStart={onTouchStart}
            role={role}
            tabIndex={tabIndex}
          >
            {option.label}
          </AutocompleteOption>
        )}
        selectOnFocus
        value={getValue({ useNullAsDefaultValue, options, value })}
      />
    </AutocompleteWrapper>
  );
}
